// ==========================================
// GOOGLE ANALYTICS 4 TRACKING UTILITIES
// ==========================================

// Declare gtag on window
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// GA4 Event Parameters
export interface GA4EventParams {
  [key: string]: string | number | boolean | undefined;
}

// Page view tracking
export const trackPageView = (
  url: string,
  title?: string,
  language: string = 'el'
): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: url,
    page_title: title,
    page_location: window.location.href,
    language: language,
    send_to: 'G-HXSXMFRTEN'
  });
};

// Custom event tracking
export const trackEvent = (
  eventName: string,
  params: GA4EventParams = {}
): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, {
    ...params,
    send_to: 'G-HXSXMFRTEN'
  });
};

// Button click tracking
export const trackButtonClick = (
  buttonText: string,
  buttonLocation: string,
  variant?: string
): void => {
  trackEvent('button_click', {
    button_text: buttonText,
    button_location: buttonLocation,
    button_variant: variant || 'default'
  });
};

// CTA click tracking
export const trackCTA = (
  ctaText: string,
  ctaSection: string,
  pagePath: string
): void => {
  trackEvent('cta_click', {
    cta_text: ctaText,
    cta_section: ctaSection,
    page_path: pagePath
  });
};

// Form tracking
export const trackFormStart = (
  formId: string,
  formName: string,
  pagePath: string
): void => {
  trackEvent('form_start', {
    form_id: formId,
    form_name: formName,
    page_path: pagePath
  });
};

export const trackFormSubmit = (
  formId: string,
  formName: string,
  pagePath: string,
  success: boolean = true
): void => {
  trackEvent(success ? 'form_submit' : 'form_submit_failure', {
    form_id: formId,
    form_name: formName,
    page_path: pagePath,
    success: success
  });
};

// Phone and email tracking
export const trackPhoneClick = (
  phoneNumber: string,
  pagePath: string
): void => {
  trackEvent('phone_click', {
    phone_number: phoneNumber,
    page_path: pagePath
  });
};

export const trackEmailClick = (
  emailAddress: string,
  pagePath: string
): void => {
  trackEvent('email_click', {
    email_address: emailAddress,
    page_path: pagePath
  });
};

// Outbound link tracking
export const trackOutboundClick = (
  url: string,
  linkText: string,
  pagePath: string
): void => {
  trackEvent('outbound_click', {
    outbound_url: url,
    link_text: linkText,
    page_path: pagePath
  });
};

// Download tracking
export const trackFileDownload = (
  fileUrl: string,
  fileName: string,
  fileType: string
): void => {
  trackEvent('file_download', {
    file_url: fileUrl,
    file_name: fileName,
    file_type: fileType
  });
};

// Scroll tracking
export const trackScroll = (
  depth: number, // 25, 50, 75, 100
  pagePath: string
): void => {
  trackEvent('scroll', {
    scroll_depth: depth,
    page_path: pagePath
  });
};

// Video tracking
export const trackVideoStart = (
  videoTitle: string,
  videoUrl: string
): void => {
  trackEvent('video_start', {
    video_title: videoTitle,
    video_url: videoUrl
  });
};

export const trackVideoComplete = (
  videoTitle: string,
  videoUrl: string,
  percentWatched: number
): void => {
  trackEvent('video_complete', {
    video_title: videoTitle,
    video_url: videoUrl,
    percent_watched: percentWatched
  });
};

// Search tracking
export const trackSearch = (
  searchTerm: string,
  pagePath: string
): void => {
  trackEvent('search', {
    search_term: searchTerm,
    page_path: pagePath
  });
};

// Engagement tracking
export const trackEngagement = (
  engagementTimeMsec: number,
  pagePath: string
): void => {
  trackEvent('user_engagement', {
    engagement_time_msec: engagementTimeMsec,
    page_path: pagePath
  });
};

// Consent management
export const updateConsent = (
  analytics: boolean,
  adStorage: boolean = false
): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('consent', 'update', {
    'analytics_storage': analytics ? 'granted' : 'denied',
    'ad_storage': adStorage ? 'granted' : 'denied',
    'ad_user_data': adStorage ? 'granted' : 'denied',
    'ad_personalization': adStorage ? 'granted' : 'denied'
  });

  // Save to localStorage
  localStorage.setItem('pp_consent_v2', analytics ? 'granted' : 'denied');
};

// Check if tracking is allowed
export const isTrackingAllowed = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    const consent = localStorage.getItem('pp_consent_v2');
    return consent === 'granted';
  } catch {
    return false;
  }
};

// Check if gtag is available
export const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && !!window.gtag;
};

// React hook for GA4 tracking
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const useGA4PageTracking = () => {
  const location = useLocation();
  const previousPathRef = useRef<string>(location.pathname);

  useEffect(() => {
    // Track page view on route change
    const currentPath = location.pathname + location.search;
    
    if (previousPathRef.current !== currentPath) {
      trackPageView(
        currentPath,
        document.title,
        document.documentElement.lang || 'el'
      );
      previousPathRef.current = currentPath;
    }
  }, [location]);
};

// Button click tracker hook
export const useGA4ButtonTracking = () => {
  const handleClick = (
    buttonText: string,
    buttonLocation: string
  ) => {
    trackButtonClick(buttonText, buttonLocation);
  };

  return { handleClick };
};

// Scroll depth tracker hook
export const useScrollTracking = (): void => {
  const location = useLocation();
  const scrollDepthsTracked = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Reset tracked depths when page changes
    scrollDepthsTracked.current.clear();

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      // Check which depths have been reached
      [25, 50, 75, 100].forEach(depth => {
        if (scrollPercent >= depth && !scrollDepthsTracked.current.has(depth)) {
          scrollDepthsTracked.current.add(depth);
          trackScroll(depth, location.pathname);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
};

// Interactive element tracking
export const bindInteractiveTracking = (): void => {
  if (typeof document === 'undefined') return;

  // Track all buttons with data-tracking attribute
  document.querySelectorAll('button[data-track], a[data-track]').forEach(el => {
    el.addEventListener('click', () => {
      const trackingData = el.getAttribute('data-track');
      if (trackingData) {
        try {
          const data = JSON.parse(trackingData);
          trackEvent(data.event || 'interaction', data.params || {});
        } catch {
          console.warn('Invalid data-track attribute');
        }
      }
    });
  });
};

// Initialize all tracking
export const initGA4Tracking = (): void => {
  bindInteractiveTracking();
};

// Enhanced link tracker for outbound and download links
export const enhanceLinks = (): void => {
  if (typeof document === 'undefined') return;

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');

    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    const url = new URL(href, window.location.origin);

    // External link
    if (url.origin !== window.location.origin) {
      trackOutboundClick(
        url.href,
        link.textContent?.trim() || 'unknown',
        window.location.pathname
      );
    }

    // Download link
    const downloadExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip', 'jpg', 'png'];
    const extension = href.split('.').pop()?.toLowerCase();
    if (downloadExtensions.includes(extension || '')) {
      trackFileDownload(
        href,
        link.textContent?.trim() || 'download',
        extension || 'file'
      );
    }
  }, { capture: true });
};

export default {
  trackPageView,
  trackEvent,
  trackButtonClick,
  trackCTA,
  trackFormStart,
  trackFormSubmit,
  trackPhoneClick,
  trackEmailClick,
  trackOutboundClick,
  trackFileDownload,
  trackScroll,
  trackVideoStart,
  trackVideoComplete,
  trackSearch,
  trackEngagement,
  updateConsent,
  isTrackingAllowed,
  isGtagAvailable,
  useGA4PageTracking,
  useGA4ButtonTracking,
  useScrollTracking,
  bindInteractiveTracking,
  enhanceLinks,
  initGA4Tracking
};
